import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";

const EDITORIAL_IMG =
  "https://images.unsplash.com/photo-1721637686340-de9f8cebda5a?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function HomePage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const featured = data.filter((p) => p.featured);
      setProducts(featured.length ? featured : data.slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Marabou Design — Heavyweight Organic Cotton T-Shirts"
        description="Marabou Design crafts heavyweight 280GSM organic cotton T-shirts with archival prints — boxy cuts, numbered drops, built to age beautifully. Shop now."
      />
      <Hero />
      <Marquee />

      <section data-testid="featured-section" className="py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">Featured drop</p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight">
                This season's heavy hitters
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="featured-view-all-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-[#4D7C0F] transition-colors"
            >
              View all tees <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {products.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="editorial-section" className="border-y border-[#E4E4DE]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2">
          <motion.div
            className="relative min-h-[420px] overflow-hidden"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={EDITORIAL_IMG} alt="Kinetik Archive editorial" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F7F7F2]/70 to-transparent" />
          </motion.div>
          <div className="flex flex-col justify-center border-l border-[#E4E4DE] px-6 py-16 sm:px-12 lg:px-16">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">The philosophy</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight leading-tight">
              Garments built like brutalist structures
            </h2>
            <p className="mt-6 text-sm sm:text-base text-zinc-500 leading-relaxed">
              Every Marabou Design tee starts as a slab: 280 grams of ring-spun organic
              cotton per square meter, cut boxy and washed until it feels already yours.
              We print with zero-waste water-based inks and release in numbered runs.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                ["280", "GSM fabric weight"],
                ["100%", "Organic cotton"],
                ["004", "Drops per year"],
              ].map(([n, label]) => (
                <div key={label}>
                  <p className="font-display text-3xl sm:text-4xl font-extrabold text-[#4D7C0F]">{n}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="editorial-about-cta"
              className="mt-10 inline-flex w-fit items-center gap-2 border border-[#E4E4DE] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-700 hover:border-[#C8FF1E] hover:text-[#4D7C0F] transition-colors"
            >
              Read the manifesto <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="cta-banner" className="py-16 sm:py-24 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">Drop 004 live now</p>
        <h2 className="mx-auto mt-4 max-w-3xl px-4 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-[0.95]">
          Once it's gone, it's archived forever.
        </h2>
        <Link
          to="/shop"
          data-testid="cta-shop-btn"
          className="mt-10 inline-flex items-center gap-2 bg-[#C8FF1E] px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] hover:text-black transition-colors"
        >
          Shop the drop <ArrowRight size={16} />
        </Link>
      </section>
    </main>
  );
}
